import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { monHocAPI, khoiAPI } from '../services/api';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ImportExcel from '../components/ImportExcel';
import { FiPlus, FiEdit2, FiTrash2, FiBook, FiUpload, FiDownload, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import * as XLSX from 'xlsx';

const MonHocPage = () => {
  const { t } = useTranslation();
  const [monHocs, setMonHocs] = useState([]);
  const [khois, setKhois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedMon, setSelectedMon] = useState(null);
  const [formData, setFormData] = useState({ tenMon: '', khoiId: '', soTietTuan: '', soTietToiDa1Buoi: '', moTa: '' });

  useEffect(() => {
    Promise.all([
      fetchMonHocs(),
      fetchKhois()
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMonHocs = async () => {
    try {
      const response = await monHocAPI.getAll();
      if (response.data.success) {
        setMonHocs(response.data.data);
      }
    } catch (error) {
      toast.error(t('mon.cannotLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchKhois = async () => {
    try {
      const response = await khoiAPI.getAll();
      if (response.data.success) {
        setKhois(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching khois:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        tenMon: formData.tenMon,
        khoiId: formData.khoiId || null,
        soTietTuan: formData.soTietTuan === '' ? 0 : Number(formData.soTietTuan),
        soTietToiDa1Buoi: formData.soTietToiDa1Buoi === '' ? null : Number(formData.soTietToiDa1Buoi),
        moTa: formData.moTa
      };

      if (selectedMon) {
        await monHocAPI.update(selectedMon.mon_id, payload);
        toast.success(t('mon.updateSuccess'));
      } else {
        await monHocAPI.create(payload);
        toast.success(t('mon.addSuccess'));
      }
      setModalOpen(false);
      setSelectedMon(null);
      setFormData({ tenMon: '', khoiId: '', soTietTuan: '', soTietToiDa1Buoi: '', moTa: '' });
      fetchMonHocs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.errorOccurred'));
    }
  };

  const handleEdit = (mon) => {
    setSelectedMon(mon);
    setFormData({
      tenMon: mon.ten_mon,
      khoiId: mon.khoi_id || '',
      soTietTuan: mon.so_tiet_tuan || '',
      soTietToiDa1Buoi: mon.so_tiet_toi_da_1_buoi ?? '',
      moTa: mon.mo_ta || ''
    });
    setModalOpen(true);
  };

  const handleDelete = (mon) => {
    setSelectedMon(mon);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await monHocAPI.delete(selectedMon.mon_id);
      toast.success(t('mon.deleteSuccess'));
      setConfirmOpen(false);
      setSelectedMon(null);
      fetchMonHocs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('mon.cannotDelete'));
    }
  };

  const openAddModal = () => {
    setSelectedMon(null);
    setFormData({ tenMon: '', khoiId: '', soTietTuan: '', soTietToiDa1Buoi: '', moTa: '' });
    setModalOpen(true);
  };

  const handleExportExcel = () => {
    const header = ['Tên môn', 'Khối', 'Số tiết/tuần', 'Số tiết tối đa 1 buổi', 'Mô tả'];
    const rows = monHocs.map(m => [
      m.ten_mon,
      m.ten_khoi || '',
      m.so_tiet_tuan || '',
      m.so_tiet_toi_da_1_buoi ?? '',
      m.mo_ta || ''
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Môn học');
    XLSX.writeFile(wb, 'mon_hoc.xlsx');
  };

  const handleImport = async (file) => {
    const response = await monHocAPI.import(file);
    if (response.data.success) {
      fetchMonHocs();
      toast.success(response.data.message);
    }
    return response;
  };

  // Sắp xếp thứ tự
  const moveUp = async (monHoc, index) => {
    if (index === 0) return;

    const updated = [...monHocs];
    const [item] = updated.splice(index, 1);
    updated.splice(index - 1, 0, item);

    try {
      await monHocAPI.reorder(updated.map((mon) => mon.mon_id));
      fetchMonHocs();
      toast.success(t('common.orderUpdated'));
    } catch (error) {
      console.error('Error reordering môn học:', error);
      toast.error(t('common.cannotUpdateOrder'));
    }
  };

  const moveDown = async (monHoc, index) => {
    if (index === monHocs.length - 1) return;

    const updated = [...monHocs];
    const [item] = updated.splice(index, 1);
    updated.splice(index + 1, 0, item);

    try {
      await monHocAPI.reorder(updated.map((mon) => mon.mon_id));
      fetchMonHocs();
      toast.success(t('common.orderUpdated'));
    } catch (error) {
      console.error('Error reordering môn học:', error);
      toast.error(t('common.cannotUpdateOrder'));
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2>{t('mon.pageTitle')} ({monHocs.length})</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={handleExportExcel}>
              <FiDownload /> Export Excel
            </button>
            <button className="btn btn-secondary" onClick={() => setImportOpen(true)}>
              <FiUpload /> {t('mon.importBtn')}
            </button>
            <button className="btn btn-primary" onClick={openAddModal}>
              <FiPlus /> {t('mon.addBtn')}
            </button>
          </div>
        </div>
        <div className="card-body">
          {monHocs.length === 0 ? (
            <div className="empty-state">
              <FiBook />
              <h3>{t('mon.empty')}</h3>
              <p>{t('mon.emptyHint')}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('mon.colOrder')}</th>
                    <th>{t('mon.colNo')}</th>
                    <th>{t('mon.colName')}</th>
                    <th>{t('mon.colKhoi')}</th>
                    <th>{t('mon.colPeriodsPerWeek')}</th>
                    <th>{t('mon.colMaxPeriodsPerSession')}</th>
                    <th>{t('mon.colDesc')}</th>
                    <th>{t('mon.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {monHocs.map((mon, index) => (
                    <tr key={mon.mon_id}>
                      <td>
                        <div className="order-controls">
                          <button
                            className="btn btn-secondary btn-icon btn-sm"
                            onClick={() => moveUp(mon, index)}
                            disabled={index === 0}
                            title={t('common.moveUp')}
                          >
                            <FiChevronUp />
                          </button>
                          <button
                            className="btn btn-secondary btn-icon btn-sm"
                            onClick={() => moveDown(mon, index)}
                            disabled={index === monHocs.length - 1}
                            title={t('common.moveDown')}
                          >
                            <FiChevronDown />
                          </button>
                        </div>
                      </td>
                      <td>{index + 1}</td>
                      <td><strong>{mon.ten_mon}</strong></td>
                      <td>{mon.ten_khoi || '-'}</td>
                      <td>{mon.so_tiet_tuan || '-'}</td>
                      <td>{mon.so_tiet_toi_da_1_buoi ?? '-'}</td>
                      <td>{mon.mo_ta || '-'}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => handleEdit(mon)}
                            title={t('common.edit')}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn btn-danger btn-icon"
                            onClick={() => handleDelete(mon)}
                            title={t('common.delete')}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal thêm/sửa */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedMon ? t('mon.modalEditTitle') : t('mon.modalAddTitle')}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              {selectedMon ? t('common.update') : t('common.addNew')}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('mon.fieldName')}</label>
            <input
              type="text"
              className="form-control"
              value={formData.tenMon}
              onChange={(e) => setFormData({ ...formData, tenMon: e.target.value })}
              placeholder={t('mon.fieldNamePlaceholder')}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('mon.fieldKhoi')}</label>
            <select
              className="form-control"
              value={formData.khoiId}
              onChange={(e) => setFormData({ ...formData, khoiId: e.target.value })}
              required
            >
              <option value="">{t('mon.selectKhoi')}</option>
              {khois.map(khoi => (
                <option key={khoi.khoi_id} value={khoi.khoi_id}>
                  {khoi.ten_khoi}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('mon.fieldPeriodsPerWeek')}</label>
            <input
              type="number"
              className="form-control"
              value={formData.soTietTuan}
              onChange={(e) => setFormData({ ...formData, soTietTuan: e.target.value })}
              placeholder="VD: 3, 4, 5..."
              min="1"
              max="10"
            />
          </div>
          <div className="form-group">
            <label>{t('mon.fieldMaxPeriodsPerSession')}</label>
            <input
              type="number"
              className="form-control"
              value={formData.soTietToiDa1Buoi}
              onChange={(e) => setFormData({ ...formData, soTietToiDa1Buoi: e.target.value })}
              placeholder="VD: 1, 2, 3..."
              min="0"
              max="10"
            />
          </div>
          <div className="form-group">
            <label>{t('mon.fieldDesc')}</label>
            <textarea
              className="form-control"
              value={formData.moTa}
              onChange={(e) => setFormData({ ...formData, moTa: e.target.value })}
              placeholder={t('mon.fieldDescPlaceholder')}
              rows={3}
            />
          </div>
        </form>
      </Modal>

      {/* Confirm xóa */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title={t('mon.confirmDeleteTitle')}
        message={t('mon.confirmDeleteMsg', { name: selectedMon?.ten_mon })}
      />

      {/* Import Excel */}
      <ImportExcel
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
        title={t('mon.importTitle')}
        templateColumns={['Tên môn', 'Khối', 'Số tiết/tuần', 'Số tiết tối đa 1 buổi', 'Mô tả']}
        templateData={[
          ['Toán', 'Khối 10', 5, 2, 'Môn học bắt buộc'],
          ['Văn', 'Khối 10', 4, 2, 'Môn học bắt buộc'],
          ['Anh', 'Khối 11', 3, 1, '']
        ]}
        templateFileName="mau_mon_hoc.xlsx"
      />
    </div>
  );
};

export default MonHocPage;
